import { executeRequest } from "@/common/utils/http/httpClient";
import { getCurrentEnvironmentVariables } from "@/lib/CONSTANTS";
import { endpointMapper } from "@/common/service/endpoint.map.js";
import { replaceAllMatchPattern } from "@/common/utils/generic/commonHelper.js";

const envSpecificApiDomain = getCurrentEnvironmentVariables().apiDomain;

const subPath = "v1";

// Service Paths urls
const adminApiPath = `${replaceAllMatchPattern(envSpecificApiDomain[subPath], endpointMapper[subPath].admin)}`;
const myPageApiPath = `${replaceAllMatchPattern(envSpecificApiDomain[subPath], endpointMapper[subPath].myPage)}`;
const bolApiPath = `${replaceAllMatchPattern(envSpecificApiDomain[subPath], endpointMapper[subPath].billOfLading)}`;
const customsReleaseApiPath = `${replaceAllMatchPattern(
  envSpecificApiDomain[subPath],
  endpointMapper[subPath].customsRelease
)}`;
const portCallApiPath = `${replaceAllMatchPattern(envSpecificApiDomain[subPath], endpointMapper[subPath].portCall)}`;
const locationApiPath = `${replaceAllMatchPattern(envSpecificApiDomain[subPath], endpointMapper[subPath].location)}`;
const workflowApiPath = `${replaceAllMatchPattern(envSpecificApiDomain[subPath], endpointMapper[subPath].workflow)}`;
const complianceApiPath = `${replaceAllMatchPattern(
  envSpecificApiDomain[subPath],
  endpointMapper[subPath].compliance
)}`;
const baplieAPIPath = `${replaceAllMatchPattern(envSpecificApiDomain[subPath], endpointMapper[subPath].baplie)}`;

//-------------------------------- My Page Service --------------------------------------------------

/**
 * getBolDetails
 * @description: Function which will trigger async call to get respective bol data
 *              based on the bol number to trigger this API
 * @returns Array of bol data object
 * @param: {*}
 */

const getBolDetails = async (bolNumber, rawResponse = false) => {
  const url = `${myPageApiPath}/aggregator/search/bol/${bolNumber}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * getComplianceDetails
 * @description: Function which will trigger async call to get respective compliance detail
 *              based on the bol number and compliance Id to trigger this API
 * @returns Array of complance data object
 */
const getComplianceDetails = async (complianceId, bolNumber, rawResponse = false) => {
  const url = `${myPageApiPath}/aggregator/search/compliance/bolref?complianceId=${complianceId}&bolNumber=${bolNumber}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

const refreshVessel = async (req_body, rawResponse = false) => {
  const url = `${myPageApiPath}/aggregator/empty-manifest-amendment`;

  return executeRequest({
    url,
    method: "POST",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

/**
 * updateAlternateVoygage
 * @description: Function which will trigger async call to update on the data
 *              based on the alternateVoyageCode and complianceType attribute Params to trigger this API
 * @returns Updated object
 */
const updateAlternateVoygage = async (params, rawResponse = false) => {
  const { alternateVoyageCode, complianceType, gsisKey, auditPayload } = params;
  let req_body = {
    alternateVoyageCode,
    complianceType,
    auditPayload
  };
  const url = `${myPageApiPath}/aggregator/portcall/alternateVoyage?id=${gsisKey}`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

/**
 * downloadManifest
 * @description:Function which will trigger async call to download manifest in excel format
 * @returns :A
 * @param : Object
 **/

const downloadManifest = async (req_body, rawResponse = true) => {
  const url = `${myPageApiPath}/aggregator/downloadExcel`;
  return executeRequest({
    url,
    method: "POST",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

/**
 * clearance location Services
 * @description:Function which will trigger customs clearance location service
 * @returns :A
 * @param : Object
 **/

const customsClearanceLocation = async ({ data, type, rawResponse = false }) => {
  const { countryCode, bolNumber } = data;
  const apiList = {
    list: {
      path: `${myPageApiPath}/aggregator/eligible-customs-clearance?countryCode=${countryCode}&bolNumber=${bolNumber}`,
      method: "GET"
    },
    save: {
      path: `${complianceApiPath}/compliance/update-customs-clearance`,
      method: "PUT"
    }
  };
  const url = `${apiList[type].path}`;
  const requestOptions = {
    url,
    method: apiList[type].method,
    rawResponse
  };

  if (apiList[type].method === "PUT") {
    requestOptions.body = JSON.stringify(data);
  }

  return executeRequest(requestOptions);
};

const updateSplitBill = async (payload, rawResponse = false) => {
  const url = `${myPageApiPath}/aggregator/update/consignment/parentTpdoc`;

  return executeRequest({
    url,
    method: "POST",
    body: JSON.stringify(payload),
    rawResponse
  });
};

/**
 * updateVesselPreReference
 * @description:Function which will trigger async call to the data for updating vessel Pre Departure /Pre Arrival reference
 * @returns :A
 * @body : req_body as input which contains references object
 **/

const updateVesselPreReference = async (req_body, rawResponse = false) => {
  const url = `${myPageApiPath}/aggregator/compliance/vesselReferences`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

/**
 * updateCargoDestination
 * @description:Function which will trigger async call to the data for updating vessel reference
 * @returns :A
 * @body : bolNumber and cargoDestination as input which contains references object
 **/

const updateCargoDestination = async (params, rawResponse = false) => {
  const { bolNumber, cargoDestination, body } = params;
  const url = `${myPageApiPath}/aggregator/updateCargoDestination?bolNumber=${bolNumber}&cargoDestination=${cargoDestination}`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(body),
    rawResponse
  });
};

/**
 * updateHSCode
 * @description:Function which will trigger async call to the data for updating HSCODE from BOL Page
 * @returns :A
 * @body : req_body as input which contains HS6Code
 **/

const updateHSCode = async (params, rawResponse = false) => {
  const { bolNumber, hscode, hsOldcode, cargoSequence, body } = params;
  const url = `${myPageApiPath}/aggregator/updateHS6CommodityDesc/?bolNumber=${bolNumber}&hs6CommodityCode=${hscode}&hs6CommodityCodeOld=${hsOldcode}&cargoSequence=${cargoSequence}`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(body),
    rawResponse
  });
};

/**
 * refreshBol
 * @description: Function which will trigger async call to get respective bol data
 *              based on the bol number to trigger this API
 * @returns Array of bol data object
 * @param: {*}
 */

const refreshBol = async (body, rawResponse = false) => {
  const url = `${myPageApiPath}/aggregator/bol/refresh`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(body),
    rawResponse
  });
};

/**
 * refreshBolFromGcss
 * @description: Function which will trigger async call to get respective bol data
 *              based on the bol number to trigger this API and do refresh data from GCSS
 * @returns Array of bol data object
 * @param: {*}
 */

export async function refreshBolFromGcss(params, rawResponse = false) {
  const { bolId, scacCode, req_body } = params;
  const url = `${myPageApiPath}/aggregator/gcssRefresh/hsCode?bolNumber=${bolId}&scacCode=${scacCode}`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(req_body),
    rawResponse
  });
}

/**
 * portcallOrphan
 * @description: Function which will trigger async call to get portcallOrphan details
 *              based on the vesselCode, voyage, siteCode, complianceId, brand, complianceType, gsisKey to trigger this API
 * @returns Array of bol data object
 * @param: {*}
 */
const portcallOrphan = async (params, rawResponse = false) => {
  const { vesselCode, voyage, siteCode, complianceId, brand, complianceType, gsisKey } = params;
  const url = `${myPageApiPath}/aggregator/portcall/orphans?vesselCode=${vesselCode}&voyage=${voyage}&siteCode=${siteCode}&complianceId=${complianceId}&brand=${brand}&complianceType=${complianceType}&gsisKey=${gsisKey}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

//-------------------------------- Bol Service --------------------------------------------------

/**
 * getRailDetails
 * @description: Function which will trigger async call to get respective rail detail
 *              based on the bol number and compliance Id to trigger this API
 * @returns Array of complance data object
 */
const getRailDetails = async (queryParams, rawResponse = false) => {
  const { complianceId, bolNumber, vesselCode, arrivalVoyage } = queryParams;
  const url = `${bolApiPath}/billoflading/containers-by-bol?bolNumber=${bolNumber}&complianceId=${complianceId}&vesselCode=${vesselCode}&arrivalVoyage=${arrivalVoyage}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * multipleBills
 * @description: Function which will trigger async call to get multiple Booking details
 *              based on the bol number to trigger this API
 * @returns Array of bol data object
 * @param: {*}
 */
const multipleBills = async (bookingNumber, rawResponse = false) => {
  const url = `${bolApiPath}/billoflading/bookingNumbers/${bookingNumber}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

// -------------------------------- Customs Release Service --------------------------------------------------

const getCustomsReleaseDetails = async (inputData, rawResponse = false) => {
  const paramsPath = generateParamsPath(inputData);
  const url = `${customsReleaseApiPath}/customs-release/get-all-containers?${paramsPath}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

// -------------------------------- Admin Service --------------------------------------------------

/**
 * baseData
 * @description: Function which will trigger async call to get respective base data
 *              from this API
 * @returns Array of base data object
 */

const getBaseData = async (rawResponse = false) => {
  const url = `${adminApiPath}/essence/basedata`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

// -------------------------------- Port call Service --------------------------------------------------
/**
 * getVesselSearchData
 * @description: Function which will trigger async call to get respective vessel data
 *              based on the manifest and countryCode to trigger this API
 * @returns Array of vessel object
 */
const getVesselSearchData = async (params, rawResponse = false) => {
  const { manifest, countryCode, fromDate, toDate, dateType, deleteManifest } = params;
  const url = `${portCallApiPath}/portcall/vessel/search?complianceType=${manifest}&countryCode=${countryCode}&fromDate=${fromDate}&toDate=${toDate}&dateType=${dateType}&deleteManifest=${deleteManifest}`;

  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * getVoyageSearchData
 * @description: Function which will trigger async call to get respective voyage service
 *              based on the query Params to trigger this API
 * @returns Array of vessel object
 */
const getVoyageSearchData = async (queryParams, rawResponse = false) => {
  const { complianceType, vesselCode } = queryParams;
  const url = `${portCallApiPath}/portcall/voyage/search?complianceType=${complianceType}&vesselCode=${vesselCode}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * getVesselVoyageList
 * @description: Function which will trigger async call to get respective voyage service
 *              based on the query Params to trigger this API
 * @returns Array of vessel object
 */
const getVesselVoyageList = async (queryParams, rawResponse = false) => {
  const { countryCode, cityCode, vesselCode, toDate, fromDate, territories } = queryParams;
  let url = `${portCallApiPath}/portcall/manifests/vesselVoyage?countryCode=${countryCode}&cityCode=${cityCode}&vesselCode=${vesselCode}&fromDate=${fromDate}&toDate=${toDate}`;
  if (territories) {
    url = url + `&territories=${territories}`;
  }
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * getPortCallList
 * @description: Function which will trigger async call to get respective voyage service
 *              based on the query Params to trigger this API
 * @returns Array of vessel object
 */
const getPortCallList = async (queryParams, rawResponse = false) => {
  const { countryCode, cityCode, vesselCode, siteCode, fromDate, toDate, voyage } = queryParams;
  const url = `${portCallApiPath}/portcall/manifests?countryCode=${countryCode}&cityCode=${cityCode}&vesselCode=${vesselCode}&siteCode=${siteCode}&fromDate=${fromDate}&toDate=${toDate}&voyage=${voyage}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * updateVesselReference
 * @description:Function which will trigger async call to the data for updating vessel reference
 * @returns :A
 * @body : req_body as input which contains references object
 **/

const updateVesselReference = async (params, rawResponse = false) => {
  const { req_body, gsisKey } = params;
  const url = `${portCallApiPath}/portcall/vesselReferences/${gsisKey}`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

// ---------------------------------------- Location Service --------------------------------------------------

/**
 * getLoadPortSearchData
 * @description: Function which will trigger async call to get respective load port
 *              based on the cityCode and countryCode to trigger this API
 * @returns Array of loadPort object
 */
const getLoadPortSearchData = async (cityCode, countryCode, searchType, rawResponse = false) => {
  const url = `${locationApiPath}/location/city/search?searchType=${searchType}&citySearchParam=${cityCode}&countryCodeParam=${countryCode}`;

  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * getAllCitySearchData
 * @description: Function which will trigger async call to get respective all city list
 *              based on the cityCode and countryCode to trigger this API
 * @returns Array of city list object
 */
const getAllCitySearchData = async (cityCode, rawResponse = false) => {
  const url = `${locationApiPath}/location/city/search?searchType=all&citySearchParam=${cityCode}&countryCodeParam=`;

  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * complianceSearchService
 * @description: Function which will trigger async call to get respective port data
 *              based on countryCode to trigger this API
 * @returns Array of vessel object
 */

const getCompliancePortSearchData = async (params, rawResponse = false) => {
  const { country: countryCode, territories, searchType = "compliancePort", citySearchParam = "" } = params;
  let url = `${locationApiPath}/location/city/search?searchType=${searchType}&citySearchParam=${citySearchParam}&countryCodeParam=${countryCode}`;
  if (territories) {
    url = url + `&territories=${territories}`;
  }
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * getCityOrSiteCode
 * @description: Function which will trigger async call to get facility data based on city code or site code
 *              if input is 5 characters, it's treated as cityCode, otherwise (more than 5) as siteCodes
 * @returns Array of facility objects with city, facility, country details
 * @param codeValue: search string entered by user (cityCode or siteCode)
 */
const getCityOrSiteCode = async (codeValue, rawResponse = false) => {
  // If 5 characters, use cities API, otherwise use facility API with siteCodes
  let url;
  if (codeValue.length === 5) {
    url = `${locationApiPath}/location/cities?cityCodes=${codeValue}`;
  } else {
    url = `${locationApiPath}/location/facility?siteCodes=${codeValue}`;
  }

  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

// -------------------------------- Workflow Service --------------------------------------------------

/**
 * updateEmailStartedTime
 * @description: Function which will trigger async call to update on the data
 *              based on the business key attribute Params to trigger this API
 * @returns Updated object
 */
const updateEmailStartedTime = async (businessKeys, rawResponse = false) => {
  const url = `${workflowApiPath}/workflowtask/update/email/senttime?businessKeys=${businessKeys}`;
  return executeRequest({
    url,
    method: "PUT",
    rawResponse
  });
};

/**
 * updateTaskStartedTime
 * @description: Function which will trigger async call to update on the data
 *              based on the business key attribute Params to trigger this API
 * @returns Updated object
 */

const updateTaskStartedTime = async (businessKeys, rawResponse = false) => {
  const url = `${workflowApiPath}/workflowtask/update/task/starttime?businessKeys=${businessKeys}`;
  return executeRequest({
    url,
    method: "PUT",
    rawResponse
  });
};

// ------------------------------------- Compliance Service --------------------------------------------------

/**
 * updateASIEmailStartedTime
 * @description: Function which will trigger async call to update on the data
 *              based on the bolNumber and compID attribute to trigger this API
 * @returns Updated object
 */
const updateASIEmailStartedTime = async (req_body, rawResponse = false) => {
  const url = `${complianceApiPath}/compliance/email-send-time-non-wfexception`;
  return executeRequest({
    url,
    method: "POST",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

/**
 * updateStage
 * @description: Function which will trigger async call to update the stage
 *              based on the payload to trigger this API
 * @returns Updated object
 */
const updateStage = async (req_body, rawResponse = true) => {
  const url = `${complianceApiPath}/compliance/allowStageChangeForRejectedBol`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

/**
 * updateComplinaceRejectedTask
 * @description:Function which will trigger async call to the data for manual rejected all submission which has complianceId to trigger this API
 * @returns :A
 * @param : Compliance Id as input params
 **/
const updateComplinaceRejectedTask = async (params, rawResponse = false) => {
  const { complianceId, auditPayload } = params;
  let req_body = {
    complianceId: complianceId,
    category: "CloseException",
    eventType: "rejectionexception",
    auditPayload
  };
  const url = `${complianceApiPath}/compliance/workflow/close`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

/**
 * updateComplinaceRejectedTaskByCompliance
 * @description:Function which will trigger async call to the data for manual rejected single submission which has complianceId and bolId to trigger this API
 * @returns :A
 * @param : Compliance Id bolId as input params
 **/
const updateComplinaceRejectedTaskByCompliance = async (newParams, rawResponse = false) => {
  const { complianceId, bolId, auditPayload } = newParams;
  let req_body = {
    complianceId: complianceId,
    category: "CloseException",
    bolNumber: bolId,
    eventType: "rejectionexception",
    auditPayload
  };
  const url = `${complianceApiPath}/compliance/workflow/close`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

/**
 * manualSubmissionService
 * @description:Function which will trigger async call to the data for manual submission which has complianceId to trigger this API
 * @returns :A
 * @param : Object
 **/

const updateComplinaceTaskApproval = async (params, rawResponse = false) => {
  const { complianceId, bols, auditPayload } = params;

  let req_body = {
    complianceId,
    status: "Approved",
    isManuallyApproved: true,
    bols,
    auditPayload
  };
  const url = `${complianceApiPath}/compliance/manualStatusUpdate`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

/**
 * updateEstimatedTimeStamp
 * @description:Function which will trigger async call to the data for updating Time Stamp
 * @returns :A
 * @body : req_body as input which contains references object
 **/

const updateEstimatedTimeStamp = async (req_body, rawResponse = false) => {
  const url = `${complianceApiPath}/compliance/updateEstimatedDate`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

const getReferenceListDetails = async req_body => {
  const url = `${complianceApiPath}/compliance/child/consignmentReference/detail`;

  return executeRequest({
    url,
    method: "POST",
    body: JSON.stringify(req_body)
  });
};

// --------------------------------------- Baplie Service --------------------------------------------------

/**
 * Baplie Services
 * @description:Function which will trigger manual submission of baplie
 * @returns :A
 * @param : Object
 **/

const baplieService = async ({ data, type, rawResponse = false }) => {
  const { baplieId, historyId } = data;
  const apiList = {
    manualSubmission: {
      path: "/baplie/submit",
      method: "POST"
    },
    baplieResponse: {
      path: `/baplie/response?baplieId=${baplieId}`,
      method: "GET"
    },
    baplieHistory: {
      path: `/baplie/containerDetails?historyId=${historyId}`,
      method: "GET"
    }
  };
  const url = `${baplieAPIPath}${apiList[type].path}`;
  const requestOptions = {
    url,
    method: apiList[type].method,
    rawResponse
  };

  if (apiList[type].method === "POST") {
    requestOptions.body = JSON.stringify(data);
  }

  return executeRequest(requestOptions);
};

// ------------------------------- Common Function --------------------------------------------

//Create Dynamic api path
const generateParamsPath = params => {
  if (params) {
    const parts = [];
    for (const [key, value] of Object.entries(params)) {
      if (value != null && value !== "") {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    }
    return parts.join("&");
  }
};

export {
  portcallOrphan,
  multipleBills,
  getCompliancePortSearchData,
  getVoyageSearchData,
  getVesselVoyageList,
  getBolDetails,
  getPortCallList,
  updateAlternateVoygage,
  updateEmailStartedTime,
  updateASIEmailStartedTime,
  updateStage,
  updateTaskStartedTime,
  updateComplinaceRejectedTask,
  updateComplinaceRejectedTaskByCompliance,
  updateComplinaceTaskApproval,
  downloadManifest,
  baplieService,
  customsClearanceLocation,
  getCityOrSiteCode,
  updateSplitBill,
  updateVesselReference,
  updateVesselPreReference,
  updateEstimatedTimeStamp,
  updateCargoDestination,
  updateHSCode,
  refreshBol,
  refreshVessel,
  getComplianceDetails,
  getRailDetails,
  getCustomsReleaseDetails,
  getBaseData,
  getVesselSearchData,
  getLoadPortSearchData,
  getAllCitySearchData,
  getReferenceListDetails
};
